import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Clock, Users, GripVertical, CalendarPlus, Download, Shield, UserPlus, Search } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOT_HEIGHT = 48; // px per 30-min slot
const SLOT_MINUTES = 30;

export default function BookingFlow() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [space, setSpace] = useState(null);
  const [layouts, setLayouts] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [userPlan, setUserPlan] = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedLayout, setSelectedLayout] = useState("");
  const [bookingType, setBookingType] = useState("hourly");
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [meetingName, setMeetingName] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [bookedForId, setBookedForId] = useState("");
  const [inviteGuests, setInviteGuests] = useState([]);
  const [newGuestName, setNewGuestName] = useState("");
  const [newGuestEmail, setNewGuestEmail] = useState("");
  const [savedGuests, setSavedGuests] = useState([]);
  const [savedGuestQuery, setSavedGuestQuery] = useState("");
  const [showSavedDropdown, setShowSavedDropdown] = useState(false);
  const savedDropdownRef = useRef(null);

  // Drag state
  const calRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/spaces/${spaceId}`),
      axios.get(`${API}/spaces/${spaceId}/layouts`)
    ]).then(([s, l]) => {
      setSpace(s.data);
      setLayouts(l.data);
      if (s.data?.type === "meeting_room") setBookingType("hourly");
      setLoading(false);
    }).catch(() => setLoading(false));
    // Fetch user plan if authenticated
    if (token) {
      axios.get(`${API}/my-plan`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setUserPlan(r.data))
        .catch(() => {});
      axios.get(`${API}/bookings/team-members`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setTeamMembers(r.data || []))
        .catch(() => {});
      axios.get(`${API}/guests/saved`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setSavedGuests(r.data || []))
        .catch(() => {});
    }
  }, [spaceId, token]);

  useEffect(() => {
    const handleClick = (e) => {
      if (savedDropdownRef.current && !savedDropdownRef.current.contains(e.target)) setShowSavedDropdown(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (selectedDate && spaceId) {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      axios.get(`${API}/spaces/${spaceId}/availability?date=${dateStr}`)
        .then(r => setAvailability(r.data)).catch(() => {});
    }
  }, [selectedDate, spaceId]);

  useEffect(() => {
    document.title = space ? `Book ${space.name} | The Rig` : "Book a Space | The Rig";
  }, [space]);

  const getSlots = useCallback(() => {
    if (!space) return [];
    const oh = space.opening_hours || { start: "08:00", end: "22:00" };
    const startH = parseInt(oh.start.split(":")[0]);
    const startM = parseInt(oh.start.split(":")[1] || "0");
    const endH = parseInt(oh.end.split(":")[0]);
    const endM = parseInt(oh.end.split(":")[1] || "0");
    const slots = [];
    const now = new Date();
    const isToday = selectedDate && format(selectedDate, "yyyy-MM-dd") === format(now, "yyyy-MM-dd");
    let h = startH, m = startM;
    while (h < endH || (h === endH && m < endM)) {
      const time = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      let isBooked = false;
      let isBlocked = false;
      let isPast = false;
      if (isToday && (h < now.getHours() || (h === now.getHours() && m <= now.getMinutes()))) {
        isPast = true;
      }
      if (availability && selectedDate) {
        const fullTime = `${format(selectedDate, "yyyy-MM-dd")}T${time}:00`;
        const slotEnd = new Date(2000, 0, 1, h, m + SLOT_MINUTES);
        const slotEndStr = `${format(selectedDate, "yyyy-MM-dd")}T${String(slotEnd.getHours()).padStart(2, "0")}:${String(slotEnd.getMinutes()).padStart(2, "0")}:00`;
        isBooked = availability.bookings?.some(b => b.start < slotEndStr && b.end > fullTime);
        isBlocked = availability.blocks?.some(b => b.start < slotEndStr && b.end > fullTime);
      }
      slots.push({ time, hour: h, minute: m, isBooked, isBlocked, isPast, unavailable: isBooked || isBlocked || isPast });
      m += SLOT_MINUTES;
      if (m >= 60) { h++; m -= 60; }
    }
    return slots;
  }, [space, availability, selectedDate]);

  const slots = getSlots();

  const slotIndexToTime = (idx) => slots[idx]?.time || "";
  const slotEndTime = (idx) => {
    if (idx + 1 < slots.length) return slots[idx + 1].time;
    const s = slots[idx];
    let m = s.minute + SLOT_MINUTES;
    let h = s.hour;
    if (m >= 60) { h++; m -= 60; }
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  const getSlotIndex = (y) => {
    if (!calRef.current) return 0;
    const rect = calRef.current.getBoundingClientRect();
    const relY = y - rect.top + calRef.current.scrollTop;
    return Math.max(0, Math.min(slots.length - 1, Math.floor(relY / SLOT_HEIGHT)));
  };

  const isSlotInRange = (idx) => {
    if (dragStart === null) return false;
    const end = dragEnd !== null ? dragEnd : dragStart;
    const lo = Math.min(dragStart, end);
    const hi = Math.max(dragStart, end);
    return idx >= lo && idx <= hi;
  };

  const hasUnavailableInRange = (lo, hi) => {
    for (let i = lo; i <= hi; i++) {
      if (slots[i]?.unavailable) return true;
    }
    return false;
  };

  const handleMouseDown = (e, idx) => {
    if (slots[idx]?.unavailable) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart(idx);
    setDragEnd(idx);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const idx = getSlotIndex(e.clientY);
    setDragEnd(idx);
  }, [isDragging, slots.length]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragStart !== null && dragEnd !== null) {
      const lo = Math.min(dragStart, dragEnd);
      const hi = Math.max(dragStart, dragEnd);
      if (hasUnavailableInRange(lo, hi)) {
        toast.error("Selected range contains unavailable slots");
        setDragStart(null);
        setDragEnd(null);
        return;
      }
      setStartTime(slotIndexToTime(lo));
      setEndTime(slotEndTime(hi));
    }
  }, [isDragging, dragStart, dragEnd, slots]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Touch support
  const handleTouchStart = (e, idx) => {
    if (slots[idx]?.unavailable) return;
    setIsDragging(true);
    setDragStart(idx);
    setDragEnd(idx);
  };
  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const idx = getSlotIndex(touch.clientY);
    setDragEnd(idx);
  };
  const handleTouchEnd = () => handleMouseUp();

  const calculatePrice = () => {
    if (!space || !startTime || !endTime) return 0;
    const pricing = space.pricing || {};
    if (bookingType === "hourly") {
      const [sh, sm] = startTime.split(":").map(Number);
      const [eh, em] = endTime.split(":").map(Number);
      const hours = (eh * 60 + em - sh * 60 - sm) / 60;
      return (pricing.hourly || 0) * hours;
    }
    if (bookingType === "daily") return pricing.daily || 0;
    if (bookingType === "monthly") return pricing.monthly || 0;
    return 0;
  };
  const price = calculatePrice();
  const hidePrices = userPlan?.hide_prices === true;
  const planHoursRemaining = userPlan?.is_unlimited ? null : (userPlan?.included_hours ? Math.max(0, userPlan.included_hours - (userPlan.hours_used || 0)) : null);

  const handleManualTimeChange = (type, val) => {
    if (type === "start") {
      setStartTime(val);
      // Update drag to match
      const idx = slots.findIndex(s => s.time === val);
      if (idx >= 0) setDragStart(idx);
    } else {
      setEndTime(val);
      // Find end slot index
      const idx = slots.findIndex(s => s.time === val);
      if (idx >= 0) setDragEnd(idx - 1 >= 0 ? idx - 1 : idx);
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate || !startTime || !endTime || !selectedLayout) {
      toast.error("Please complete all fields");
      return;
    }
    if (!isAuthenticated && (!guestName || !guestEmail)) {
      toast.error("Guest name and email are required");
      return;
    }
    setSubmitting(true);
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const payload = {
      space_id: spaceId,
      layout_id: selectedLayout,
      start_datetime: `${dateStr}T${startTime}:00`,
      end_datetime: `${dateStr}T${endTime}:00`,
      booking_type: bookingType,
      meeting_name: meetingName.trim() || undefined,
      booked_for_id: bookedForId && bookedForId !== user?.id ? bookedForId : undefined,
      guest_name: !isAuthenticated ? guestName : undefined,
      guest_email: !isAuthenticated ? guestEmail : undefined,
      notes: notes || undefined,
    };
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/bookings`, payload, { headers });
      setConfirmedBooking(res.data);
      // Pre-register invited guests
      if (isAuthenticated && inviteGuests.length > 0 && res.data.id) {
        for (const g of inviteGuests) {
          try {
            await axios.post(`${API}/guests/pre-register`, {
              name: g.name, email: g.email || null,
              booking_id: res.data.id, purpose: meetingName || "Meeting",
            }, { headers });
          } catch {}
        }
      }
      setStep(4);
      toast.success(res.data.status === "confirmed" ? "Booking confirmed!" : "Booking submitted! Awaiting payment.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-12"><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;
  if (!space) return <div className="max-w-4xl mx-auto px-6 py-12 text-center"><p>Space not found</p></div>;

  const steps = ["Date & Time", "Layout", "Confirm"];

  // Build booked blocks for the calendar visualization
  const bookedBlocks = (availability?.bookings || []).map(b => {
    const bStart = b.start.slice(11, 16);
    const bEnd = b.end.slice(11, 16);
    const startIdx = slots.findIndex(s => s.time >= bStart);
    const endIdx = slots.findIndex(s => s.time >= bEnd);
    const actualEnd = endIdx === -1 ? slots.length : endIdx;
    return { startIdx: startIdx === -1 ? 0 : startIdx, endIdx: actualEnd, status: b.status, start: bStart, end: bEnd };
  });

  const blockedBlocks = (availability?.blocks || []).map(b => {
    const bStart = b.start.slice(11, 16);
    const bEnd = b.end.slice(11, 16);
    const startIdx = slots.findIndex(s => s.time >= bStart);
    const endIdx = slots.findIndex(s => s.time >= bEnd);
    const actualEnd = endIdx === -1 ? slots.length : endIdx;
    return { startIdx: startIdx === -1 ? 0 : startIdx, endIdx: actualEnd, reason: b.reason, start: bStart, end: bEnd };
  });

  const generateEndTimeOptions = () => {
    if (!startTime || !slots.length) return [];
    const startIdx = slots.findIndex(s => s.time === startTime);
    if (startIdx < 0) return [];
    const options = [];
    for (let i = startIdx + 1; i <= slots.length; i++) {
      const t = i < slots.length ? slots[i].time : slotEndTime(slots.length - 1);
      options.push(t);
      if (i < slots.length && slots[i].unavailable) break;
    }
    return options;
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 animate-fade-in">
      <title>{space?.name ? `Book ${space.name} | The Rig` : "Book a Space | The Rig"}</title>
      <meta name="description" content={space?.name ? `Reserve ${space.name} at The Rig. Choose your date, time, and layout. Capacity: ${space.capacity} people. Hourly, daily, and monthly rates available.` : "Reserve a space at The Rig coworking. Choose your date, time, and layout."} />
      <meta property="og:title" content={space?.name ? `Book ${space.name} | The Rig` : "Book a Space | The Rig"} />
      <meta property="og:description" content={space?.name ? `Reserve ${space.name} at The Rig coworking space.` : "Reserve a space at The Rig coworking."} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={space?.photos?.[0] || "https://customer-assets.emergentagent.com/job_book-spaces-2/artifacts/2dhwnmm0_therig.png"} />
      <Button variant="ghost" onClick={() => navigate(`/spaces/${spaceId}`)} className="mb-4 -ml-2" data-testid="back-to-space-btn">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to {space.name}
      </Button>

      <h1 className="font-heading font-bold text-3xl mb-2" data-testid="booking-flow-title">Book {space.name}</h1>
      <p className="text-muted-foreground mb-8">Complete the steps below to reserve your space</p>

      {/* Steps */}
      <div className="flex items-center gap-2 mb-10" data-testid="booking-steps">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              step > i + 1 ? "bg-primary text-primary-foreground" : step === i + 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            }`}>
              {step > i + 1 ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`text-sm hidden sm:inline ${step === i + 1 ? "font-medium text-foreground" : "text-muted-foreground"}`}>{s}</span>
            {i < steps.length - 1 && <div className="w-8 sm:w-16 h-px bg-border" />}
          </div>
        ))}
      </div>

      {/* Plan Banner */}
      {userPlan && step < 4 && (
        <div className="mb-4 p-4 rounded-lg bg-rig-teal/5 border border-rig-teal/20" data-testid="plan-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Shield className="h-4 w-4 text-rig-teal" />
              <span className="font-semibold text-rig-charcoal">{userPlan.name} Plan</span>
              {userPlan.hide_prices && <Badge className="bg-rig-teal/10 text-rig-teal text-xs">Pricing included</Badge>}
            </div>
            <div className="text-sm text-rig-charcoal/50">
              {userPlan.is_unlimited ? "Unlimited hours" : `${planHoursRemaining?.toFixed(1)}h remaining this month`}
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Date & Time */}
      {step === 1 && (
        <Card className="p-6 border border-border rounded-none" data-testid="step-date-time">
          <h2 className="font-heading font-semibold text-xl mb-6">Select Date & Time</h2>

          <div className="mb-4">
            <Label className="mb-2 block">Booking Type</Label>
            {space.type === "meeting_room" ? (
              <div className="text-sm text-muted-foreground p-2 bg-muted/50 rounded border">
                {hidePrices ? "Hourly booking" : `Hourly — XCG ${space.pricing?.hourly}/hr`}
              </div>
            ) : (
              <Select value={bookingType} onValueChange={setBookingType}>
                <SelectTrigger data-testid="booking-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">{hidePrices ? "Hourly" : `Hourly (XCG ${space.pricing?.hourly}/hr)`}</SelectItem>
                  <SelectItem value="daily">{hidePrices ? "Daily" : `Daily (XCG ${space.pricing?.daily}/day)`}</SelectItem>
                  <SelectItem value="monthly">{hidePrices ? "Monthly" : `Monthly (XCG ${space.pricing?.monthly}/mo)`}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Calendar picker */}
            <div data-testid="booking-calendar">
              <Label className="mb-2 block">Date</Label>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => {
                  setSelectedDate(d);
                  setStartTime("");
                  setEndTime("");
                  setDragStart(null);
                  setDragEnd(null);
                }}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                className="rounded-lg border shadow-sm"
              />
            </div>

            {/* Day Calendar View */}
            {selectedDate && slots.length > 0 && (
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    Drag to select time range
                  </Label>
                  <span className="text-xs text-muted-foreground">{format(selectedDate, "EEEE, MMM d")}</span>
                </div>

                {/* Manual time inputs */}
                <div className="flex gap-3 mb-4" data-testid="manual-time-inputs">
                  <div className="flex-1">
                    <Label className="text-xs mb-1 block">Start Time</Label>
                    <Select value={startTime} onValueChange={(v) => handleManualTimeChange("start", v)}>
                      <SelectTrigger className="h-9" data-testid="manual-start-time">
                        <SelectValue placeholder="Start" />
                      </SelectTrigger>
                      <SelectContent>
                        {slots.filter(s => !s.unavailable).map(s => (
                          <SelectItem key={s.time} value={s.time}>{s.time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs mb-1 block">End Time</Label>
                    <Select value={endTime} onValueChange={(v) => handleManualTimeChange("end", v)}>
                      <SelectTrigger className="h-9" data-testid="manual-end-time">
                        <SelectValue placeholder="End" />
                      </SelectTrigger>
                      <SelectContent>
                        {generateEndTimeOptions().map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Visual Day Calendar */}
                <div
                  ref={calRef}
                  className="relative border border-border rounded overflow-y-auto select-none"
                  style={{ maxHeight: "400px" }}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  data-testid="day-calendar"
                >
                  {slots.map((slot, idx) => {
                    const inRange = isSlotInRange(idx);
                    const isSelected = startTime && endTime && slot.time >= startTime && slot.time < endTime;
                    const showHour = slot.minute === 0;

                    return (
                      <div
                        key={idx}
                        className={`flex items-stretch border-b border-border/50 cursor-pointer transition-colors duration-75 ${
                          slot.unavailable
                            ? "bg-red-50 cursor-not-allowed"
                            : inRange || isSelected
                            ? "bg-primary/10"
                            : "hover:bg-muted/50"
                        }`}
                        style={{ height: `${SLOT_HEIGHT}px` }}
                        onMouseDown={(e) => handleMouseDown(e, idx)}
                        onTouchStart={(e) => handleTouchStart(e, idx)}
                        data-testid={`cal-slot-${slot.time}`}
                      >
                        {/* Time label */}
                        <div className={`w-16 flex-shrink-0 flex items-center justify-end pr-3 text-xs border-r border-border/50 ${
                          showHour ? "font-medium text-foreground" : "text-muted-foreground/60"
                        }`}>
                          {showHour ? slot.time : ""}
                        </div>

                        {/* Slot content */}
                        <div className="flex-1 relative flex items-center px-3">
                          {slot.unavailable && (
                            <div className="flex items-center gap-2">
                              <div className={`w-1 h-6 rounded ${slot.isPast ? "bg-gray-300" : slot.isBooked ? "bg-red-400" : "bg-amber-400"}`} />
                              <span className="text-xs text-muted-foreground">
                                {slot.isPast ? "Past" : slot.isBooked ? "Reserved" : "Blocked"}
                              </span>
                            </div>
                          )}
                          {(inRange || isSelected) && !slot.unavailable && (
                            <div className="w-1 h-6 rounded bg-primary" />
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Overlay blocks for booked ranges */}
                  {bookedBlocks.map((b, i) => (
                    <div
                      key={`booked-${i}`}
                      className="absolute right-0 left-16 bg-red-100/80 border-l-2 border-red-400 flex items-center px-3 pointer-events-none z-10"
                      style={{
                        top: `${b.startIdx * SLOT_HEIGHT}px`,
                        height: `${(b.endIdx - b.startIdx) * SLOT_HEIGHT}px`,
                      }}
                    >
                      <div className="text-xs text-red-700 font-medium truncate">
                        Reserved {b.start} - {b.end}
                      </div>
                    </div>
                  ))}

                  {/* Overlay blocks for blocked ranges */}
                  {blockedBlocks.map((b, i) => (
                    <div
                      key={`blocked-${i}`}
                      className="absolute right-0 left-16 bg-amber-100/80 border-l-2 border-amber-400 flex items-center px-3 pointer-events-none z-10"
                      style={{
                        top: `${b.startIdx * SLOT_HEIGHT}px`,
                        height: `${(b.endIdx - b.startIdx) * SLOT_HEIGHT}px`,
                      }}
                    >
                      <div className="text-xs text-amber-700 font-medium truncate">
                        Blocked: {b.reason}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Legend */}
                <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1"><div className="w-3 h-3 bg-primary/20 border border-primary rounded-sm" />Selected</div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-100 border border-red-400 rounded-sm" />Reserved</div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 bg-amber-100 border border-amber-400 rounded-sm" />Blocked</div>
                </div>

                {/* Selection display */}
                {startTime && endTime && (
                  <div className="mt-3 p-3 bg-muted/50 rounded border" data-testid="selected-time-display">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">
                        {startTime} - {endTime}
                      </span>
                      <Badge variant="secondary" className="text-xs ml-auto">
                        {(() => {
                          const [sh, sm] = startTime.split(":").map(Number);
                          const [eh, em] = endTime.split(":").map(Number);
                          const mins = eh * 60 + em - sh * 60 - sm;
                          const h = Math.floor(mins / 60);
                          const m = mins % 60;
                          return h > 0 ? `${h}h ${m > 0 ? m + "m" : ""}` : `${m}m`;
                        })()}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Estimated: <span className="font-medium text-foreground">{hidePrices ? "Included in plan" : `XCG ${price.toFixed(2)}`}</span>
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end mt-6">
            <Button
              onClick={() => {
                if (!selectedDate || !startTime || !endTime) { toast.error("Select date and time range"); return; }
                setStep(2);
              }}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none"
              data-testid="step1-next-btn"
            >
              Next <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* Step 2: Layout */}
      {step === 2 && (
        <Card className="p-6 border border-border rounded-none" data-testid="step-layout">
          <h2 className="font-heading font-semibold text-xl mb-6">Choose Layout</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {layouts.map((l, i) => (
              <button
                key={l.id}
                className={`p-4 rounded-lg border-2 text-left transition-all ${selectedLayout === l.layout_id ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}
                onClick={() => setSelectedLayout(l.layout_id)}
                data-testid={`layout-option-${i}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">{l.layout_name}</h3>
                  {selectedLayout === l.layout_id && <Check className="h-5 w-5 text-primary" />}
                </div>
                <p className="text-xs text-muted-foreground mb-2">{l.layout_description}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span><Users className="h-3 w-3 inline mr-1" />Capacity: {l.capacity}</span>
                  {l.setup_buffer_minutes > 0 && <span><Clock className="h-3 w-3 inline mr-1" />{l.setup_buffer_minutes}min setup</span>}
                </div>
              </button>
            ))}
          </div>
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="step2-back-btn"><ArrowLeft className="mr-2 h-4 w-4" />Back</Button>
            <Button onClick={() => { if (!selectedLayout) { toast.error("Select a layout"); return; } setStep(3); }} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none" data-testid="step2-next-btn">
              Next <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && (
        <Card className="p-6 border border-border rounded-none" data-testid="step-confirm">
          <h2 className="font-heading font-semibold text-xl mb-6">Review & Confirm</h2>
          <div className="bg-muted/50 rounded-lg p-5 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">Space:</span> <span className="font-medium">{space.name}</span></div>
              {space.type !== "meeting_room" && <div><span className="text-muted-foreground">Type:</span> <Badge variant="secondary" className="ml-1 text-xs">{bookingType}</Badge></div>}
              <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{selectedDate ? format(selectedDate, "MMM d, yyyy") : ""}</span></div>
              <div><span className="text-muted-foreground">Time:</span> <span className="font-mono font-medium">{startTime} - {endTime}</span></div>
              <div><span className="text-muted-foreground">Layout:</span> <span className="font-medium">{layouts.find(l => l.layout_id === selectedLayout)?.layout_name}</span></div>
              <div><span className="text-muted-foreground">Price:</span> <span className="font-bold text-primary text-lg">{hidePrices ? "Included in plan" : `XCG ${price.toFixed(2)}`}</span></div>
              {bookedForId && bookedForId !== user?.id && (
                <div className="col-span-full"><span className="text-muted-foreground">Booking for:</span> <span className="font-medium text-rig-teal">{teamMembers.find(m => m.id === bookedForId)?.name}</span></div>
              )}
            </div>
          </div>

          {!isAuthenticated && (
            <div className="space-y-4 mb-6">
              <h3 className="font-medium">Guest Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Full Name *</Label><Input value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="John Doe" data-testid="guest-name-input" /></div>
                <div><Label>Email *</Label><Input type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="john@example.com" data-testid="guest-email-input" /></div>
              </div>
            </div>
          )}

          {isAuthenticated && teamMembers.length > 1 && (
            <div className="mb-6" data-testid="booking-for-section">
              <Label className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                Booking For
              </Label>
              <Select value={bookedForId || user?.id || ""} onValueChange={setBookedForId}>
                <SelectTrigger data-testid="booking-for-select"><SelectValue placeholder="Select team member" /></SelectTrigger>
                <SelectContent>
                  {teamMembers.map(m => (
                    <SelectItem key={m.id} value={m.id} data-testid={`team-member-${m.id}`}>
                      {m.name}{m.id === user?.id ? " (You)" : ""} — {m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Book this room on behalf of a team member. Hours are deducted from your team's shared plan.</p>
            </div>
          )}

          <div className="mb-6">
            <Label>Meeting Name (optional)</Label>
            <Input value={meetingName} onChange={e => setMeetingName(e.target.value)} placeholder="e.g. Weekly Standup, Client Pitch" data-testid="booking-meeting-name-input" />
            <p className="text-xs text-muted-foreground mt-1">Shown on room displays and in confirmation emails</p>
          </div>

          {isAuthenticated && (
            <div className="mb-6" data-testid="guest-invite-section">
              <Label className="flex items-center gap-2 mb-2">
                <UserPlus className="h-4 w-4 text-muted-foreground" />
                Pre-register Guests (optional)
              </Label>
              <p className="text-xs text-muted-foreground mb-3">Guests will receive an email invite and can check in when they arrive.</p>
              {inviteGuests.length > 0 && (
                <div className="space-y-2 mb-3">
                  {inviteGuests.map((g, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded border text-sm">
                      <span>{g.name} {g.email && <span className="text-muted-foreground">({g.email})</span>}</span>
                      <button
                        className="text-xs text-red-500 hover:underline"
                        onClick={() => setInviteGuests(prev => prev.filter((_, idx) => idx !== i))}
                        data-testid={`remove-guest-${i}`}
                      >Remove</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Saved guests quick-add */}
              {savedGuests.length > 0 && (
                <div className="relative mb-3" ref={savedDropdownRef}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      className="pl-9 h-9 text-sm"
                      placeholder="Quick add from frequent guests..."
                      value={savedGuestQuery}
                      onChange={e => { setSavedGuestQuery(e.target.value); setShowSavedDropdown(true); }}
                      onFocus={() => setShowSavedDropdown(true)}
                      data-testid="saved-guest-search"
                    />
                  </div>
                  {showSavedDropdown && (
                    <div className="absolute z-20 w-full mt-1 bg-background border rounded-md shadow-lg max-h-40 overflow-y-auto" data-testid="saved-guest-dropdown">
                      {savedGuests
                        .filter(sg => !savedGuestQuery || sg.name.toLowerCase().includes(savedGuestQuery.toLowerCase()))
                        .filter(sg => !inviteGuests.some(ig => ig.name === sg.name && ig.email === sg.email))
                        .map(sg => (
                          <button
                            key={sg.id}
                            className="w-full text-left px-4 py-2.5 hover:bg-muted/50 border-b last:border-0 transition-colors"
                            onClick={() => {
                              setInviteGuests(prev => [...prev, { name: sg.name, email: sg.email }]);
                              setSavedGuestQuery(""); setShowSavedDropdown(false);
                            }}
                            data-testid={`saved-guest-${sg.id}`}
                          >
                            <div className="text-sm font-medium">{sg.name}</div>
                            {sg.email && <div className="text-xs text-muted-foreground">{sg.email}</div>}
                          </button>
                        ))}
                      {savedGuests.filter(sg => !savedGuestQuery || sg.name.toLowerCase().includes(savedGuestQuery.toLowerCase())).filter(sg => !inviteGuests.some(ig => ig.name === sg.name && ig.email === sg.email)).length === 0 && (
                        <div className="px-4 py-2.5 text-xs text-muted-foreground">No matching saved guests</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  placeholder="Guest name"
                  value={newGuestName}
                  onChange={e => setNewGuestName(e.target.value)}
                  className="flex-1"
                  data-testid="invite-guest-name"
                />
                <Input
                  placeholder="Email (optional)"
                  value={newGuestEmail}
                  onChange={e => setNewGuestEmail(e.target.value)}
                  className="flex-1"
                  data-testid="invite-guest-email"
                />
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => {
                    if (!newGuestName.trim()) return;
                    setInviteGuests(prev => [...prev, { name: newGuestName.trim(), email: newGuestEmail.trim() }]);
                    setNewGuestName(""); setNewGuestEmail("");
                  }}
                  data-testid="add-guest-btn"
                >Add</Button>
              </div>
            </div>
          )}

          <div className="mb-6">
            <Label>Notes (optional)</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any special requirements..." data-testid="booking-notes-input" />
          </div>

          {!isAuthenticated && (
            <div className="bg-accent/30 border border-accent rounded-lg p-4 mb-6 text-sm" data-testid="payment-notice">
              <p className="font-medium text-accent-foreground mb-1">Payment Required</p>
              <p className="text-muted-foreground">Your booking will be held pending payment. You'll receive payment instructions via email.</p>
            </div>
          )}

          <Separator className="my-6" />
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)} data-testid="step3-back-btn"><ArrowLeft className="mr-2 h-4 w-4" />Back</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-foreground text-background hover:bg-foreground/90 rounded-none transition-transform active:scale-95" data-testid="confirm-booking-btn">
              {submitting ? "Submitting..." : "Confirm Booking"} {!submitting && <Check className="ml-2 h-4 w-4" />}
            </Button>
          </div>
        </Card>
      )}

      {/* Step 4: Success + Calendar */}
      {step === 4 && confirmedBooking && (
        <Card className="p-8 border border-border rounded-none text-center" data-testid="step-success">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-rig-green/15 mb-6">
            <Check className="h-8 w-8 text-rig-green" strokeWidth={2.5} />
          </div>
          <h2 className="font-heading font-bold text-2xl mb-2 text-rig-charcoal">
            {confirmedBooking.status === "confirmed" ? "Booking Confirmed!" : "Booking Submitted!"}
          </h2>
          <p className="text-rig-charcoal/50 mb-8 max-w-md mx-auto">
            {confirmedBooking.status === "confirmed"
              ? "Your space is booked. A confirmation email with a calendar invite has been sent."
              : "Your booking is pending payment. You'll receive instructions via email."}
          </p>

          <div className="bg-gray-50 rounded-xl p-5 mb-8 max-w-sm mx-auto text-left">
            <div className="grid grid-cols-2 gap-3 text-sm">
              {confirmedBooking.meeting_name && (<>
                <div className="text-rig-charcoal/40">Meeting</div>
                <div className="font-bold text-rig-charcoal" data-testid="confirmed-meeting-name">{confirmedBooking.meeting_name}</div>
              </>)}
              {confirmedBooking.booked_for_name && (<>
                <div className="text-rig-charcoal/40">Booked For</div>
                <div className="font-semibold text-rig-charcoal" data-testid="confirmed-booked-for">{confirmedBooking.booked_for_name}</div>
              </>)}
              <div className="text-rig-charcoal/40">Space</div>
              <div className="font-semibold text-rig-charcoal">{space?.name}</div>
              <div className="text-rig-charcoal/40">Date</div>
              <div className="font-semibold text-rig-charcoal">{selectedDate ? format(selectedDate, "MMM d, yyyy") : ""}</div>
              <div className="text-rig-charcoal/40">Time</div>
              <div className="font-mono font-semibold text-rig-charcoal">{startTime} - {endTime}</div>
              <div className="text-rig-charcoal/40">Reference</div>
              <div className="font-mono text-sm text-rig-charcoal">{confirmedBooking.id?.slice(0, 8)}</div>
              <div className="text-rig-charcoal/40">Price</div>
              <div className="font-heading font-bold text-rig-orange text-lg">{confirmedBooking.plan_covered ? "Included in plan" : `XCG ${confirmedBooking.price?.toFixed(2)}`}</div>
            </div>
          </div>

          <p className="text-xs text-rig-charcoal/40 uppercase tracking-wider mb-4 font-medium">Add to your calendar</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 mb-8">
            <Button
              variant="outline"
              className="rounded-full border-gray-200 hover:border-rig-orange hover:text-rig-orange"
              data-testid="add-google-calendar-btn"
              onClick={() => {
                const dateStr = format(selectedDate, "yyyyMMdd");
                const startFormatted = `${dateStr}T${startTime.replace(":", "")}00`;
                const endFormatted = `${dateStr}T${endTime.replace(":", "")}00`;
                const title = encodeURIComponent(`Booking: ${space?.name}`);
                const details = encodeURIComponent(`The Rig Coworking - ${space?.name}\nRef: ${confirmedBooking.id?.slice(0, 8)}\nPrice: XCG ${confirmedBooking.price?.toFixed(2)}`);
                const location = encodeURIComponent("The Rig Coworking Space");
                window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startFormatted}/${endFormatted}&details=${details}&location=${location}`, "_blank");
              }}
            >
              <CalendarPlus className="h-4 w-4 mr-2" />Google Calendar
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-gray-200 hover:border-rig-teal hover:text-rig-teal"
              data-testid="add-outlook-calendar-btn"
              onClick={() => {
                const dateStr = format(selectedDate, "yyyy-MM-dd");
                const subject = encodeURIComponent(`Booking: ${space?.name}`);
                const body = encodeURIComponent(`The Rig Coworking - ${space?.name}\nRef: ${confirmedBooking.id?.slice(0, 8)}\nPrice: XCG ${confirmedBooking.price?.toFixed(2)}`);
                const location = encodeURIComponent("The Rig Coworking Space");
                window.open(`https://outlook.office.com/calendar/0/action/compose?subject=${subject}&startdt=${dateStr}T${startTime}:00&enddt=${dateStr}T${endTime}:00&body=${body}&location=${location}`, "_blank");
              }}
            >
              <CalendarPlus className="h-4 w-4 mr-2" />Outlook / Office 365
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-gray-200 hover:border-rig-green hover:text-rig-green"
              data-testid="download-ical-btn"
              onClick={() => {
                window.open(`${API}/bookings/${confirmedBooking.id}/ical`, "_blank");
              }}
            >
              <Download className="h-4 w-4 mr-2" />Download .ics
            </Button>
          </div>

          <Separator className="my-6" />
          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate("/spaces")} className="rounded-full" data-testid="back-to-spaces-btn">
              Browse More Spaces
            </Button>
            {isAuthenticated && (
              <Button onClick={() => navigate("/dashboard")} className="bg-rig-charcoal text-white hover:bg-rig-charcoal/90 rounded-full" data-testid="go-to-dashboard-btn">
                My Bookings
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
